import { PSApiOperations } from "@/api/PSApiOperations"
import { queryClient } from "@/api/constants"
import { PhotoEditorPlanResponseDTO } from "@/api/dto/pixieset-payment"
import { GetUserResponseBody, PixiesetAccountInfoStatus } from "@/api/dto/users"
import { PIXIESET_PAYMENT_PLANS_QUERY_KEY } from "@/api/payments-api"
import { analytics } from "./amplitude"
import {
	getPixiesetUpgradeTabId,
	PixiesetSubscriptionSnapshot,
	PIXIESET_SUBSCRIPTION_SNAPSHOT_TTL_MS,
	consumePixiesetSubscriptionSnapshot,
	setPixiesetSubscriptionSnapshot,
} from "@/utils/pixieset-upgrade"

type PixiesetBillingInterval = "monthly" | "yearly"

export interface SubscriptionSucceededTransition {
	fromStatusName: string
	currentInterval: PixiesetBillingInterval
	currentBillingPlan: string
}

export interface SubscriptionSucceededEventProperties extends Record<string, unknown> {
	plan: string
	from_status: string
	price: number
	interval: PixiesetBillingInterval
	currency: string
}

interface ResolvedPaidPlan {
	plan: string
	price: number
	interval: PixiesetBillingInterval
	currency: string
}

export const getPixiesetBillingInterval = (
	billingPlan?: string | null,
): PixiesetBillingInterval | null => {
	if (!billingPlan) {
		return null
	}

	const normalizedPlan = billingPlan.toLowerCase()
	if (normalizedPlan.includes("monthly") || normalizedPlan.includes("month")) {
		return "monthly"
	}
	if (normalizedPlan.includes("yearly") || normalizedPlan.includes("year")) {
		return "yearly"
	}

	return null
}

export const isPixiesetSubscriptionSnapshotExpired = (
	snapshot: Pick<PixiesetSubscriptionSnapshot, "timestamp">,
	now = Date.now(),
): boolean => now - snapshot.timestamp > PIXIESET_SUBSCRIPTION_SNAPSHOT_TTL_MS

export const buildPixiesetSubscriptionSnapshot = (
	user?: GetUserResponseBody,
): PixiesetSubscriptionSnapshot | null => {
	if (!user) {
		return null
	}

	return {
		tabId: getPixiesetUpgradeTabId(),
		timestamp: Date.now(),
		accountInfoStatus:
			user.pixiesetSubscription?.accountInfoStatus ?? PixiesetAccountInfoStatus.Free,
		billingPlan: user.pixiesetSubscription?.billingPlan || null,
	}
}

export const storeCurrentPixiesetSubscriptionSnapshot = (user?: GetUserResponseBody): boolean => {
	const snapshot = buildPixiesetSubscriptionSnapshot(user)
	if (!snapshot) {
		return false
	}

	return setPixiesetSubscriptionSnapshot(snapshot.tabId, snapshot)
}

const getReadablePlanName = (
	status: PixiesetAccountInfoStatus,
	billingPlan?: string | null,
): string | null => {
	if (status === PixiesetAccountInfoStatus.Free) {
		return "Free"
	}
	if (status !== PixiesetAccountInfoStatus.Upgraded) {
		return null
	}

	const interval = getPixiesetBillingInterval(billingPlan)
	if (interval === "monthly") {
		return "Pro - Monthly"
	}
	if (interval === "yearly") {
		return "Pro - Yearly"
	}

	return null
}

const resolvePaidPlan = (
	plans: PhotoEditorPlanResponseDTO[],
	interval: PixiesetBillingInterval,
): ResolvedPaidPlan | null => {
	const matchedPlan = plans.find((plan) => {
		if (plan.general_name.toLowerCase() !== "pro") {
			return false
		}

		if (interval === "monthly") {
			return plan.interval === "month"
		}

		return plan.interval === "year"
	})

	if (!matchedPlan) {
		return null
	}

	return {
		plan: matchedPlan.name,
		price: matchedPlan.amount,
		interval,
		currency: matchedPlan.currency.toUpperCase(),
	}
}

export const preflightSubscriptionSucceededTransition = (
	previousSnapshot: Pick<PixiesetSubscriptionSnapshot, "accountInfoStatus" | "billingPlan">,
	currentUser: GetUserResponseBody,
): SubscriptionSucceededTransition | null => {
	const currentStatus =
		currentUser.pixiesetSubscription?.accountInfoStatus ?? PixiesetAccountInfoStatus.Free
	const currentBillingPlan = currentUser.pixiesetSubscription?.billingPlan || null
	if (currentStatus !== PixiesetAccountInfoStatus.Upgraded || !currentBillingPlan) {
		return null
	}

	const currentInterval = getPixiesetBillingInterval(currentBillingPlan)
	if (!currentInterval) {
		return null
	}

	if (previousSnapshot.accountInfoStatus === PixiesetAccountInfoStatus.Free) {
		return {
			fromStatusName: "Free",
			currentInterval,
			currentBillingPlan,
		}
	}

	if (previousSnapshot.accountInfoStatus !== PixiesetAccountInfoStatus.Upgraded) {
		return null
	}

	const previousReadablePlan = getReadablePlanName(
		previousSnapshot.accountInfoStatus,
		previousSnapshot.billingPlan,
	)
	const previousInterval = getPixiesetBillingInterval(previousSnapshot.billingPlan)
	if (!previousReadablePlan || !previousInterval || previousInterval === currentInterval) {
		return null
	}

	return {
		fromStatusName: previousReadablePlan,
		currentInterval,
		currentBillingPlan,
	}
}

export const buildSubscriptionSucceededEventProperties = (
	previousSnapshot: Pick<PixiesetSubscriptionSnapshot, "accountInfoStatus" | "billingPlan">,
	currentUser: GetUserResponseBody,
	plans: PhotoEditorPlanResponseDTO[],
): SubscriptionSucceededEventProperties | null => {
	const transition = preflightSubscriptionSucceededTransition(previousSnapshot, currentUser)
	if (!transition) {
		return null
	}

	const resolvedPlan = resolvePaidPlan(plans, transition.currentInterval)
	if (!resolvedPlan) {
		return null
	}

	return {
		...resolvedPlan,
		from_status: transition.fromStatusName,
	}
}

const getPixiesetPaymentPlans = async (): Promise<PhotoEditorPlanResponseDTO[]> => {
	const cachedPlans = queryClient.getQueryData<PhotoEditorPlanResponseDTO[]>(
		PIXIESET_PAYMENT_PLANS_QUERY_KEY,
	)
	if (cachedPlans) {
		return cachedPlans
	}

	return queryClient.fetchQuery({
		queryKey: PIXIESET_PAYMENT_PLANS_QUERY_KEY,
		queryFn: PSApiOperations.getPhotoEditorPlans,
	})
}

export const trackSubscriptionSucceededIfNeeded = async (
	currentUser?: GetUserResponseBody,
): Promise<boolean> => {
	const tabId = getPixiesetUpgradeTabId()
	const previousSnapshot = consumePixiesetSubscriptionSnapshot(tabId)
	if (!previousSnapshot) {
		return false
	}

	if (isPixiesetSubscriptionSnapshotExpired(previousSnapshot)) {
		return false
	}

	if (!currentUser) {
		return false
	}

	const transition = preflightSubscriptionSucceededTransition(previousSnapshot, currentUser)
	if (!transition) {
		return false
	}

	let plans: PhotoEditorPlanResponseDTO[]
	try {
		plans = await getPixiesetPaymentPlans()
	} catch (error) {
		console.warn("Failed to fetch Pixieset plans for subscription tracking.", error)
		return false
	}

	const eventProperties = buildSubscriptionSucceededEventProperties(
		previousSnapshot,
		currentUser,
		plans,
	)
	if (!eventProperties) {
		return false
	}

	analytics.trackEvent({
		label: "subscription_succeeded",
		type: "event",
		customProperties: eventProperties,
	})
	return true
}
